import { useEffect, useId, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import joinClassNames from '@/utils/functions/joinClassNames/joinClassNames';

import styles from './styles.module.css';

import type { ReactNode, SyntheticEvent } from 'react';

interface TutorialDialogProps {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  className?: string;
  children: ReactNode;
}

/**
 * Coque de modale des tutoriels vidéo, bâtie sur l'élément natif `<dialog>` ouvert par `showModal()`.
 *
 * Ce n'est pas la modale du uikit : elle n'en reprend que l'apparence (titre, sous-titre, croix de fermeture).
 * Le `<dialog>` natif apporte la top layer (au-dessus de tout sans z-index), l'inertie du reste de la page
 * et la fermeture par Échap, nécessaires à un lecteur vidéo ouvert depuis une carte ou une autre modale.
 * Le focus est rendu à l'élément déclencheur à la fermeture.
 */
const TutorialDialog = ({ isOpen, title, subtitle, onClose, className, children }: TutorialDialogProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const subtitleId = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen) {
      // Garde sur dialog.open : StrictMode rejoue l'effet, et showModal() sur un dialog ouvert lève InvalidStateError.
      if (!dialog.open) {
        const activeElement = document.activeElement;
        triggerRef.current = activeElement instanceof HTMLElement ? activeElement : null;
        dialog.showModal();
      }
      closeButtonRef.current?.focus();
    } else {
      // Le dialog peut déjà être fermé nativement (Chrome ferme sans « cancel » un 2e Échap sans activation) :
      // le focus est restitué dans tous les cas.
      if (dialog.open) dialog.close();
      triggerRef.current?.focus();
      triggerRef.current = null;
    }
  }, [isOpen]);

  // Écouteur natif plutôt qu'un onClick JSX : <dialog> n'est pas un élément interactif (règle Sonar S6848).
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleBackdropClick = (event: MouseEvent) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      const isInside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!isInside) onClose();
    };
    dialog.addEventListener('click', handleBackdropClick);
    return () => dialog.removeEventListener('click', handleBackdropClick);
  }, [onClose]);

  const handleCancel = (event: SyntheticEvent<HTMLDialogElement>) => {
    event.preventDefault();
    onClose();
  };

  const handleNativeClose = () => {
    if (isOpen) onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      className={joinClassNames(styles.tutorialDialog, className)}
      aria-labelledby={titleId}
      aria-describedby={subtitle ? subtitleId : undefined}
      onCancel={handleCancel}
      onClose={handleNativeClose}
    >
      <div className={styles.header}>
        <div className={styles.headerTexts}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          {subtitle && (
            <p id={subtitleId} className={styles.subtitle}>
              {subtitle}
            </p>
          )}
        </div>
        <button
          ref={closeButtonRef}
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label={t('videoTutorials.closeButtonLabel')}
        >
          <i className="totem-icon totem-icon-cross-big" aria-hidden="true" />
        </button>
      </div>
      {isOpen && children}
    </dialog>
  );
};

export default TutorialDialog;
